// Actions d'administration sur un collecteur : approuver (quatre yeux), refuser, désactiver, réactiver, révoquer (en deux
// temps), lier une source. L'API n'a pas de simulation pour ces routes : l'écran montre ce qui va se passer (« Simuler »),
// puis demande la confirmation. Il n'existe AUCUNE action de suppression.
// Sécurité : l'empreinte saisie reste dans l'état d'un seul champ, n'est ni journalisée ni conservée (le champ est vidé
// dès l'envoi), et n'est jamais envoyée ailleurs qu'à l'API ; champ « text », sans mémorisation par le navigateur.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type ReactNode } from 'react';
import { ErreurApi, nouvelleCle } from '../api/client';
import type { CollecteurDetail, RaisonRevocation } from '../api/types';
import { AlerteNomSuspect, Chargement, IdCourt } from '../composants/communs';
import { useEkera } from '../contexte';
import {
  caracteresSaisis,
  estMonApprobation,
  identiteAvecId,
  expliquerErreurCollecteur,
  LIBELLE_RAISON_REVOCATION,
  NOMBRE_HEXA_EMPREINTE,
  nomCollecteur,
  normaliserEmpreinte,
} from '../domaine/collecteurs';
import { dateFr } from '../domaine/format';

function Alerte({ erreur, titre }: { erreur: unknown; titre: string }) {
  return (
    <div className="encadre erreur" role="alert">
      <p>
        <b>{titre}</b>
      </p>
      <p>{expliquerErreurCollecteur(erreur)}</p>
    </div>
  );
}

/** Une écriture à la fois ; la même clé d'idempotence sert à un nouvel essai après une coupure réseau seulement. */
function useEcriture(apres: () => Promise<void>) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const cle = useRef<string | null>(null);
  const envoi = useRef(false); // garde synchrone : un second clic arrivé avant le rendu ne part pas (O78)
  async function lancer(appel: (cleIdempotence: string) => Promise<unknown>): Promise<boolean> {
    if (envoi.current) return false;
    envoi.current = true;
    setEnCours(true);
    setErreur(null);
    cle.current ??= nouvelleCle();
    try {
      await appel(cle.current);
      cle.current = null;
      await apres();
      return true;
    } catch (e) {
      if (e instanceof ErreurApi && e.statut !== 0) cle.current = null;
      setErreur(e);
      return false;
    } finally {
      envoi.current = false;
      setEnCours(false);
    }
  }
  return { enCours, erreur, lancer, oublier: () => setErreur(null) };
}

/** Après une écriture : la liste, la fiche et les sources (liaisons) sont relues. */
function useRelire() {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: ['collecteurs'] });
    await qc.invalidateQueries({ queryKey: ['carte'] });
  };
}

// ------------------------------------------------------------------ approbation (quatre yeux)

export function Approbation({ c }: { c: CollecteurDetail }) {
  const { client, moi } = useEkera();
  const relire = useRelire();
  const w = useEcriture(relire);
  const idEmp = useId();
  const idAide = useId();
  const idNom = useId();
  const [saisie, setSaisie] = useState('');
  // O77 : le nom proposé vient du collecteur (non fiable) ; l'administrateur saisit lui-même le nom, champ vide au départ.
  const [nom, setNom] = useState('');
  const deja = c.approuve_par.find((a) => estMonApprobation(a, moi));
  const requises = c.quatre_yeux ? 2 : 1;
  const valide = normaliserEmpreinte(saisie) !== null;
  const nomOk = nom.trim().length >= 1 && nom.length <= 80;
  const bloque = moi.compte_service ? 'Un compte de service ne peut pas approuver un enrôlement.' : deja ? 'Vous avez déjà approuvé cette demande : une autre personne doit la confirmer.' : '';
  const saisi = caracteresSaisis(saisie);
  return (
    <section className="panneau" aria-labelledby="t-approbation">
      <h2 id="t-approbation">Approuver cette demande</h2>
      <div className="encadre info">
        <p>
          <b>Ekera n’affiche pas l’empreinte d’une demande en attente.</b> L’empreinte s’affiche sur le serveur du collecteur :
          lisez-la là-bas et saisissez-la ici. Elle prouve que la demande vient bien de votre serveur et pas d’un tiers.
        </p>
      </div>
      <div className="encadre attention">
        <p>
          <b>Quatre yeux.</b> {requises === 2 ? 'Deux administrateurs distincts doivent approuver' : 'Un administrateur doit approuver'}{' '}
          : approbations reçues, {c.approbations} sur {requises}. Le même administrateur ne peut pas approuver deux fois, et un
          compte de service ne peut pas approuver.
        </p>
        {c.approuve_par.length > 0 && c.approbations < requises && (
          <p>
            {c.approuve_par.length === 1 ? 'Première approbation' : 'Approbations'} donnée{c.approuve_par.length === 1 ? '' : 's'} par{' '}
            {c.approuve_par.map((a) => identiteAvecId(a)).join(' et ')} le {dateFr(c.approuve_par[0]!.le)} ; il manque une
            approbation d’une autre personne.
          </p>
        )}
        {c.approuve_par.length > 0 && (
          <ul style={{ margin: '6px 0 0', paddingLeft: '1.2em' }}>
            {c.approuve_par.map((a) => (
              <li key={`${a.par}-${a.le}`}>
                {a.par_nom ? <b>{a.par_nom}</b> : <>le compte <code>{a.par}</code></>}
                <IdCourt x={a} />
                {a.par_nom && (
                  <small className="note">
                    {' '}
                    (identifiant : <code>{a.par}</code>
                    {a.emetteur ? <>, émetteur : <code>{a.emetteur}</code></> : null})
                  </small>
                )}{' '}
                a approuvé le {dateFr(a.le)} (nom retenu : « {a.nom} »)
                <AlerteNomSuspect x={a} />
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="encadre info" aria-label="Demande à approuver" role="region">
        <p>
          <b>Cette approbation concerne la demande suivante.</b> Identifiant : <code>{c.id}</code> · compte lié (sujet) :{' '}
          <code>{c.sujet}</code>
          {c.sujet_emetteur ? (
            <>
              {' '}
              · émetteur : <code>{c.sujet_emetteur}</code>
            </>
          ) : null} · demandée le {dateFr(c.cree_le)}. Vérifiez-les avant de saisir l’empreinte.
        </p>
      </div>
      <form
        className="pile"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          const empreinte = normaliserEmpreinte(saisie);
          if (!empreinte || !nomOk || bloque || w.enCours) return;
          setSaisie(''); // l'empreinte n'est gardée ni pendant, ni après l'envoi
          void w.lancer((cle) => client.approuverCollecteur(c.id, empreinte, nom.trim(), cle));
        }}
      >
        <div className="champ">
          <label htmlFor={idEmp}>
            Empreinte courte lue sur le serveur du collecteur{' '}
            <span className="aide">— {NOMBRE_HEXA_EMPREINTE} caractères (0 à 9, a à f) ; espaces, tirets et majuscules sont acceptés</span>
          </label>
          <input
            id={idEmp}
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            maxLength={40}
            value={saisie}
            aria-invalid={saisie !== '' && !valide}
            aria-describedby={idAide}
            onChange={(e) => setSaisie(e.target.value)}
            disabled={!!bloque}
          />
          <span id={idAide} className="aide" role="status">
            {saisie === ''
              ? 'Rien de saisi.'
              : valide
                ? 'Format reconnu.'
                : `Format non reconnu : ${saisi} caractères saisis sur ${NOMBRE_HEXA_EMPREINTE}, ou caractère hors 0 à 9 et a à f.`}
          </span>
        </div>
        <div className="champ">
          <label htmlFor={idNom}>
            Nom du collecteur <span className="aide">— le site ou le logiciel, 80 caractères au plus ; à saisir vous-même : le nom proposé par la demande n’est pas vérifié</span>
          </label>
          <input id={idNom} type="text" autoComplete="off" maxLength={80} value={nom} onChange={(e) => setNom(e.target.value)} />
        </div>
        {bloque && <p className="note">{bloque}</p>}
        {w.erreur != null && <Alerte erreur={w.erreur} titre="L’approbation n’a pas abouti." />}
        <div className="ligne">
          <button type="submit" className="btn principal" disabled={!valide || !nomOk || !!bloque || w.enCours}>
            Approuver
          </button>
        </div>
      </form>
    </section>
  );
}

// ------------------------------------------------------------------ actions avec motif (simulation puis confirmation)

function ActionAvecMotif({
  titre,
  intro,
  declencheur,
  simulation,
  confirmer,
  executer,
  danger,
}: {
  titre: string;
  intro: ReactNode;
  declencheur: string;
  simulation: (motif: string) => ReactNode;
  confirmer: string;
  executer: (motif: string, cle: string) => Promise<unknown>;
  danger?: boolean;
}) {
  const relire = useRelire();
  const w = useEcriture(relire);
  const [ouvert, setOuvert] = useState(false);
  const [etape, setEtape] = useState<'saisie' | 'confirmation'>('saisie');
  const [motif, setMotif] = useState('');
  const idMotif = useId();
  const motifOk = motif.trim().length >= 1 && motif.length <= 256;
  const ferme = () => {
    setOuvert(false);
    setEtape('saisie');
    setMotif('');
    w.oublier();
  };
  return (
    <section className="panneau" aria-labelledby={`${idMotif}-t`}>
      <h2 id={`${idMotif}-t`}>{titre}</h2>
      {!ouvert ? (
        <>
          <p className="note">{intro}</p>
          <p>
            <button type="button" className="btn" onClick={() => setOuvert(true)}>
              {declencheur}
            </button>
          </p>
        </>
      ) : etape === 'saisie' ? (
        <form
          className="pile"
          onSubmit={(e) => {
            e.preventDefault();
            if (motifOk) setEtape('confirmation');
          }}
        >
          <div className="champ">
            <label htmlFor={idMotif}>
              Motif <span className="aide">— obligatoire, gardé dans le journal de sécurité (256 caractères au plus)</span>
            </label>
            <textarea id={idMotif} value={motif} maxLength={256} onChange={(e) => setMotif(e.target.value)} required />
          </div>
          <div className="ligne">
            <button type="submit" className="btn principal" disabled={!motifOk}>
              Simuler
            </button>
            <button type="button" className="btn" onClick={ferme}>
              Annuler
            </button>
          </div>
        </form>
      ) : (
        <div className="pile">
          <div className={`encadre ${danger ? 'attention' : 'info'}`} role="region" aria-label="Simulation">
            <p>
              <b>Ce qui va se passer.</b> {simulation(motif.trim())}
            </p>
            <p>
              Motif noté au journal : <q>{motif.trim()}</q>
            </p>
          </div>
          {w.erreur != null && <Alerte erreur={w.erreur} titre="L’API a refusé le changement." />}
          <div className="ligne">
            <button
              type="button"
              className="btn principal"
              disabled={w.enCours}
              onClick={() => void w.lancer((cle) => executer(motif.trim(), cle)).then((ok) => ok && ferme())}
            >
              {confirmer}
            </button>
            <button type="button" className="btn" onClick={() => setEtape('saisie')} disabled={w.enCours}>
              Modifier le motif
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function Refus({ c }: { c: CollecteurDetail }) {
  const { client } = useEkera();
  return (
    <ActionAvecMotif
      titre="Refuser cette demande"
      intro="Un refus est définitif : le collecteur devra refaire une demande d’enrôlement avec une nouvelle clé. La demande reste visible dans la liste, avec son motif."
      declencheur="Refuser…"
      simulation={() => 'La demande sera refusée, définitivement. Elle n’est pas supprimée : elle reste dans la liste avec son journal.'}
      confirmer="Confirmer le refus"
      executer={(motif, cle) => client.refuserCollecteur(c.id, motif, cle)}
      danger
    />
  );
}

export function Arret({ c }: { c: CollecteurDetail }) {
  const { client } = useEkera();
  const actif = c.etat === 'actif';
  return (
    <ActionAvecMotif
      titre={actif ? 'Arrêter ce collecteur' : 'Reprendre ce collecteur'}
      intro={
        actif
          ? 'Une désactivation est réversible : le collecteur ne dépose plus de lectures, ses sources restent liées mais ne lui sont plus servies. Rien n’est supprimé.'
          : 'Ce collecteur est désactivé. Le réactiver lui rend ses sources et lui permet de déposer de nouveau ses lectures ; rien de l’historique n’a été perdu.'
      }
      declencheur={actif ? 'Désactiver…' : 'Réactiver…'}
      simulation={() =>
        actif
          ? `« ${nomCollecteur(c)} » ne déposera plus de lectures et ne recevra plus ses ${c.sources} source(s). Les liaisons restent ; une réactivation est possible à tout moment.`
          : `« ${nomCollecteur(c)} » redeviendra actif : il retrouvera ses ${c.sources} source(s) et pourra de nouveau déposer ses lectures.`
      }
      confirmer={actif ? 'Confirmer la désactivation' : 'Confirmer la réactivation'}
      executer={(motif, cle) => (actif ? client.desactiverCollecteur(c.id, motif, cle) : client.reactiverCollecteur(c.id, motif, cle))}
    />
  );
}

// ------------------------------------------------------------------ révocation (définitive, deux temps)

/**
 * « 2026-10-21T08:30 » (champ de date, lu comme une heure UTC : le fuseau est écrit à côté du champ) en horodatage
 * « 2026-10-21T08:30:00Z » ; `null` si illisible ou si la date n'existe pas (30 février).
 */
export function versHorodatageUtc(saisie: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?$/.exec(saisie);
  if (!m) return null;
  const iso = `${m[1]}${m[2] ?? ':00'}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 19) + 'Z' !== iso) return null;
  return iso;
}

export function Revocation({ c }: { c: CollecteurDetail }) {
  const { client } = useEkera();
  const relire = useRelire();
  const w = useEcriture(relire);
  const [ouvert, setOuvert] = useState(false);
  const [etape, setEtape] = useState<'saisie' | 'confirmation'>('saisie');
  const [raison, setRaison] = useState<RaisonRevocation | ''>(''); // O78 : aucune valeur par défaut
  const [compris, setCompris] = useState(false);
  const [comprisToutes, setComprisToutes] = useState(false);
  const [motif, setMotif] = useState('');
  const [depuisLocal, setDepuisLocal] = useState('');
  const idMotif = useId();
  const idRaison = useId();
  const idDepuis = useId();
  const idCompris = useId();
  const idToutes = useId();
  const depuis = raison === 'compromission' ? versHorodatageUtc(depuisLocal) : null;
  const futur = depuis !== null && new Date(depuis).getTime() > Date.now();
  const depuisIllisible = raison === 'compromission' && depuisLocal !== '' && depuis === null;
  const motifOk = motif.trim().length >= 1 && motif.length <= 256;
  const sansDate = raison === 'compromission' && depuis === null;
  const pret = raison !== '' && motifOk && !futur && !depuisIllisible;
  const peutConfirmer = compris && (!sansDate || comprisToutes) && !w.enCours;
  const ferme = () => {
    setOuvert(false);
    setEtape('saisie');
    setMotif('');
    setDepuisLocal('');
    setRaison('');
    setCompris(false);
    setComprisToutes(false);
    w.oublier();
  };
  return (
    <section className="panneau" aria-labelledby="t-revocation">
      <h2 id="t-revocation">Révoquer ce collecteur</h2>
      {!ouvert ? (
        <>
          <p className="note">
            La révocation est <b>définitive</b> : toutes ses clés sont révoquées et ses sources sont déliées. Pour reprendre, le
            collecteur devra être enrôlé de nouveau (nouvelle demande, nouvelle clé, deux approbations), puis ses sources liées
            une à une. À utiliser si la clé ou le serveur est compromis ou perdu.
          </p>
          <p>
            <button type="button" className="btn" onClick={() => setOuvert(true)}>
              Révoquer…
            </button>
          </p>
        </>
      ) : etape === 'saisie' ? (
        <form
          className="pile"
          onSubmit={(e) => {
            e.preventDefault();
            if (pret) setEtape('confirmation');
          }}
        >
          <div className="champ">
            <label htmlFor={idRaison}>Raison</label>
            <select id={idRaison} value={raison} required onChange={(e) => setRaison(e.target.value as RaisonRevocation | '')}>
              <option value="">— choisir une raison —</option>
              {(Object.keys(LIBELLE_RAISON_REVOCATION) as RaisonRevocation[]).map((r) => (
                <option key={r} value={r}>
                  {LIBELLE_RAISON_REVOCATION[r]}
                </option>
              ))}
            </select>
          </div>
          {raison === 'compromission' && (
            <div className="champ">
              <label htmlFor={idDepuis}>
                Date de compromission déclarée (heure UTC){' '}
                <span className="aide">
                  — facultative, en heure UTC (temps universel, à Paris : 1 h de moins qu’en hiver, 2 h de moins qu’en été) ; sans date,
                  TOUTES les lectures déposées par ce collecteur depuis sa création sont ignorées
                </span>
              </label>
              <input id={idDepuis} type="datetime-local" value={depuisLocal} onChange={(e) => setDepuisLocal(e.target.value)} aria-invalid={futur || depuisIllisible} />
              {futur && <span className="aide" role="alert">La date ne peut pas être dans le futur.</span>}
            </div>
          )}
          <div className="champ">
            <label htmlFor={idMotif}>
              Motif <span className="aide">— obligatoire, gardé dans le journal de sécurité (256 caractères au plus)</span>
            </label>
            <textarea id={idMotif} value={motif} maxLength={256} onChange={(e) => setMotif(e.target.value)} required />
          </div>
          <div className="ligne">
            <button type="submit" className="btn principal" disabled={!pret}>
              Continuer
            </button>
            <button type="button" className="btn" onClick={ferme}>
              Annuler
            </button>
          </div>
        </form>
      ) : (
        <div className="pile">
          <div className="encadre attention" role="region" aria-label="Confirmation de la révocation">
            <p>
              <b>Dernière étape : cette révocation est définitive.</b>
            </p>
            <ul style={{ margin: '6px 0 0', paddingLeft: '1.2em' }}>
              <li>
                « {nomCollecteur(c)} » ne pourra plus jamais signer de dépôt ; toutes ses clés sont révoquées.
              </li>
              <li>
                Ses {c.sources} source(s) liée(s) seront déliées : plus aucun collecteur ne les lira tant qu’elles ne sont pas liées à
                un autre.
                {c.sources_liees.length > 0 && (
                  <ul aria-label="Sources déliées" style={{ margin: '4px 0 0', paddingLeft: '1.2em' }}>
                    {c.sources_liees.map((s) => (
                      <li key={`${s.carte}-${s.source}`}>
                        {s.nom} <small>(carte {s.carte})</small>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
              {raison === 'compromission' && (
                <li>
                  Les lectures déposées par ce collecteur{' '}
                  {depuis ? (
                    <>depuis le {dateFr(depuis)} (heure UTC : {depuis})</>
                  ) : (
                    <>
                      <b>depuis sa création : TOUTES ses lectures, sans exception (aucune date déclarée)</b>
                    </>
                  )}{' '}
                  seront <b>marquées suspectes et ignorées</b> dans l’état des sources. Elles ne sont <b>jamais effacées</b> : elles
                  restent consultables.
                </li>
              )}
              {raison !== '' && <li>Raison : {LIBELLE_RAISON_REVOCATION[raison]}.</li>}
            </ul>
            <p>
              Motif noté au journal : <q>{motif.trim()}</q>
            </p>
          </div>
          {/* O78 : cases à cocher avant le bouton, pour qu'un double-clic sur « Continuer » ne confirme rien */}
          <div className="champ">
            <label htmlFor={idCompris}>
              <input id={idCompris} type="checkbox" checked={compris} disabled={w.enCours} onChange={(e) => setCompris(e.target.checked)} /> Je
              comprends que c’est définitif
            </label>
          </div>
          {sansDate && (
            <div className="champ">
              <label htmlFor={idToutes}>
                <input id={idToutes} type="checkbox" checked={comprisToutes} disabled={w.enCours} onChange={(e) => setComprisToutes(e.target.checked)} />{' '}
                Je comprends que, sans date, toutes les lectures de ce collecteur depuis sa création seront ignorées
              </label>
            </div>
          )}
          {w.erreur != null && <Alerte erreur={w.erreur} titre="La révocation n’a pas abouti." />}
          <div className="ligne">
            <button
              type="button"
              className="btn principal"
              disabled={!peutConfirmer}
              onClick={() => {
                if (raison === '') return;
                void w
                  .lancer((cle) => client.revoquerCollecteur(c.id, { motif: motif.trim(), raison, compromisDepuis: depuis }, cle))
                  .then((ok) => ok && ferme());
              }}
            >
              Révoquer définitivement
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setEtape('saisie');
                setCompris(false);
                setComprisToutes(false);
              }}
              disabled={w.enCours}
            >
              Retour
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ lier une source

export function LierSource({ c }: { c: CollecteurDetail }) {
  const { client, cid } = useEkera();
  const relire = useRelire();
  const w = useEcriture(relire);
  const sources = useQuery({ queryKey: ['carte', cid, 'sources'], queryFn: () => client.sources(cid) });
  const [ouvert, setOuvert] = useState(false);
  const [etape, setEtape] = useState<'saisie' | 'confirmation'>('saisie');
  const [sid, setSid] = useState('');
  const [motif, setMotif] = useState('');
  const idSource = useId();
  const idMotif = useId();
  const possibles = (sources.data ?? []).filter((s) => s.mode === 'connecteur' && s.collecteur?.id !== c.id);
  const choisie = possibles.find((s) => s.id === sid);
  const motifOk = motif.trim().length >= 1 && motif.length <= 256;
  const ferme = () => {
    setOuvert(false);
    setEtape('saisie');
    setSid('');
    setMotif('');
    w.oublier();
  };
  return (
    <section className="panneau" aria-labelledby="t-lier">
      <h2 id="t-lier">Lier une source</h2>
      {!ouvert ? (
        <>
          <p className="note">
            Une source est lue par un seul collecteur ; sans collecteur, elle n’est lue par personne. Ce que le collecteur lit reste borné par sa configuration sur site.
          </p>
          <p>
            <button type="button" className="btn" onClick={() => setOuvert(true)}>
              Lier une source…
            </button>
          </p>
        </>
      ) : sources.isPending ? (
        <Chargement quoi="des sources" />
      ) : sources.error ? (
        <Alerte erreur={sources.error} titre="Impossible de lire les sources." />
      ) : possibles.length === 0 ? (
        <>
          <p>Aucune source de cette carte n’est disponible à la liaison (toutes sont déjà liées à ce collecteur, ou lues par dépôt d’export).</p>
          <button type="button" className="btn" onClick={ferme}>
            Fermer
          </button>
        </>
      ) : etape === 'saisie' ? (
        <form
          className="pile"
          onSubmit={(e) => {
            e.preventDefault();
            if (choisie && motifOk) setEtape('confirmation');
          }}
        >
          <div className="champ">
            <label htmlFor={idSource}>Source à lier</label>
            <select id={idSource} value={sid} onChange={(e) => setSid(e.target.value)} required>
              <option value="">— choisir une source —</option>
              {possibles.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nom}
                  {s.collecteur ? ` (lue par ${s.collecteur.nom ?? s.collecteur.id})` : ' (aucun collecteur)'}
                </option>
              ))}
            </select>
          </div>
          <div className="champ">
            <label htmlFor={idMotif}>
              Motif <span className="aide">— obligatoire, gardé dans l’historique de la source (256 caractères au plus)</span>
            </label>
            <textarea id={idMotif} value={motif} maxLength={256} onChange={(e) => setMotif(e.target.value)} required />
          </div>
          <div className="ligne">
            <button type="submit" className="btn principal" disabled={!choisie || !motifOk}>
              Simuler
            </button>
            <button type="button" className="btn" onClick={ferme}>
              Annuler
            </button>
          </div>
        </form>
      ) : (
        <div className="pile">
          <div className="encadre info" role="region" aria-label="Simulation">
            <p>
              <b>Ce qui va se passer.</b> « {choisie?.nom} » sera lue par « {nomCollecteur(c)} »
              {choisie?.collecteur ? `, au lieu de « ${choisie.collecteur.nom ?? choisie.collecteur.id} »` : ''}. Le changement est inscrit dans l’historique de la source.
            </p>
            <p>
              Motif noté : <q>{motif.trim()}</q>
            </p>
          </div>
          {w.erreur != null && <Alerte erreur={w.erreur} titre="La liaison n’a pas abouti." />}
          <div className="ligne">
            <button
              type="button"
              className="btn principal"
              disabled={w.enCours}
              onClick={() => void w.lancer((cle) => client.lierSource(cid, sid, c.id, motif.trim(), cle)).then((ok) => ok && ferme())}
            >
              Confirmer la liaison
            </button>
            <button type="button" className="btn" onClick={() => setEtape('saisie')} disabled={w.enCours}>
              Modifier
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
